import { useState, type FormEvent } from "react";
import {
  Alert,
  Button,
  Card,
  CardHeader,
  EmptyState,
  Field,
  Input,
  LoadMore,
  LoadingBlock,
  PageHeader,
  Stat,
  StatGroup,
  StatusBadge,
  Table,
  Td,
  Th,
} from "../components/ui";
import { ApiError } from "../lib/api";
import { TxLink } from "../components/TxLink";
import { formatDate } from "../lib/format";
import { formatMoney } from "../lib/money";
import { PAYOUT_STATES, canCancel } from "../lib/states";
import { useTranslation } from "react-i18next";
import {
  useCancelPayout,
  useOverview,
  usePayouts,
  useRequestPayout,
  useSetPayoutAddress,
} from "../lib/queries";

export function PayoutsPage() {
  const { t } = useTranslation("payouts");
  const { t: ts } = useTranslation("states");
  const overview = useOverview(30);
  const payouts = usePayouts();
  const request = useRequestPayout();
  const cancel = useCancelPayout();
  const setAddress = useSetPayoutAddress();

  const [address, setAddressValue] = useState("");
  const [addressError, setAddressError] = useState<string | null>(null);
  const [requestError, setRequestError] = useState<string | null>(null);

  if (overview.isPending) return <LoadingBlock />;
  if (overview.error) return <Alert>{overview.error.message}</Alert>;

  const { affiliate, program, balance } = overview.data;
  const belowMinimum = balance.payableMicros < program.minPayoutMicros;
  /*
   * The pages flattened once, rather than at each place they are read.
   *
   * The open-payout check reads the *first* page and that is sufficient by
   * construction: the list is newest-first and there can be at most one payout
   * in flight — `requestPayout` refuses a second — so if one exists it is on the
   * page already loaded. Paging further to find it would be looking for
   * something that cannot be down there.
   */
  const payoutRows = payouts.data?.pages.flatMap((page) => page.data) ?? [];
  // Read off the list rather than tracked separately, so the button and the
  // table can never disagree about whether something is in flight.
  const openPayout = payoutRows.find(
    (payout) =>
      payout.status === "requested" ||
      payout.status === "approved" ||
      payout.status === "processing",
  );

  async function handleAddress(event: FormEvent) {
    event.preventDefault();
    setAddressError(null);
    try {
      await setAddress.mutateAsync(address.trim());
      setAddressValue("");
    } catch (cause) {
      /*
       * The API's own sentence, and here it is the useful part. A refusal is
       * either "that is not a wallet address", "that does not pass its own
       * checksum" or "that is the zero address" — three different mistakes with
       * three different fixes, and one generic message would hide all of them.
       */
      setAddressError(
        cause instanceof ApiError ? cause.message : t("saveFailed"),
      );
    }
  }

  async function handleRequest() {
    setRequestError(null);
    try {
      await request.mutateAsync();
    } catch (cause) {
      // The two refusals are "below the minimum" and "one is already in
      // progress", both written as a sentence by the API. Shown as they are.
      setRequestError(cause instanceof ApiError ? cause.message : t("requestFailed"));
    }
  }

  return (
    <>
      <PageHeader
        title={t("title")}
        /* The minimum is not repeated here: it is said where it acts, under the
           request button that is disabled because of it. */
        description={t("description")}
      />

      {/*
       * The same divided panel the overview uses, constrained rather than left
       * to stretch. Two boxed tiles across a wide viewport put ~550px of white
       * behind each figure, which reads as a layout that lost its third column.
       */}
      <div className="max-w-xl">
        <StatGroup columns={2}>
          <Stat
            boxed={false}
            size="lg"
            label={t("available")}
            value={formatMoney(balance.payableMicros)}
            hint={t("availableHint")}
            tone={balance.payableMicros === 0 ? "muted" : "default"}
          />
          <Stat
            boxed={false}
            size="lg"
            label={t("held")}
            value={formatMoney(balance.pendingMicros)}
            hint={t("heldHint", { count: program.holdDays })}
            tone={balance.pendingMicros === 0 ? "muted" : "default"}
          />
        </StatGroup>
      </div>

      <Card>
        <CardHeader
          title={t("wallet.title")}
          description={t("wallet.description")}
        />
        <div className="space-y-4 px-4 py-4">
          {affiliate.payoutAddress ? (
            <div className="flex flex-wrap items-center gap-2 text-[13px]">
              {/*
               * Shown in its checksummed form, which the API returns rather than
               * echoing back what was typed. That capitalisation is a checksum,
               * so an affiliate comparing this against their wallet is checking
               * something real — and it is the only chance anybody gets to spot
               * a wrong address before money goes to it.
               */}
              <span className="font-mono break-all text-fg">{affiliate.payoutAddress}</span>
              {affiliate.payoutAddressConfirmed ? (
                <StatusBadge tone="live" label={t("wallet.confirmed")} />
              ) : (
                /*
                 * "Confirmed on your first payout" rather than "unverified",
                 * because there is nothing for the affiliate to do about it. The
                 * flag is set by a transfer that actually reached the address —
                 * evidence rather than a claim — so the honest thing to say is
                 * what will confirm it.
                 */
                <StatusBadge tone="neutral" label={t("wallet.unconfirmed")} />
              )}
            </div>
          ) : (
            <Alert tone="warn">{t("wallet.missing")}</Alert>
          )}

          {openPayout ? (
            /*
             * Refused while a payout is open rather than allowed and applied to
             * the next one. The address is copied onto the payout when it is
             * requested, so changing it here would not redirect anything — and
             * telling somebody their new address is live while money is on its
             * way to the old one is the kind of true statement that reads as a
             * lie afterwards.
             */
            <p className="text-[12px] text-fg-subtle">
              {t("wallet.locked")}
            </p>
          ) : (
            <form onSubmit={(event) => void handleAddress(event)} className="space-y-3">
              <Field
                label={affiliate.payoutAddress ? t("wallet.change") : t("wallet.label")}
                htmlFor="payout-address"
                error={addressError}
                hint={t("wallet.hint")}
              >
                <Input
                  id="payout-address"
                  value={address}
                  onChange={(event) => setAddressValue(event.target.value)}
                  placeholder="0x…"
                  required
                  /* Off on all four. A wallet address is not a word, and a
                     phone that capitalises the first character or offers to
                     autocorrect it produces an address that fails a checksum
                     the affiliate cannot see. */
                  autoComplete="off"
                  autoCapitalize="off"
                  autoCorrect="off"
                  spellCheck={false}
                  className="font-mono"
                />
              </Field>
              <Button
                type="submit"
                variant="secondary"
                loading={setAddress.isPending}
                disabled={address.trim().length === 0}
              >
                {t("wallet.save")}
              </Button>
            </form>
          )}
        </div>
      </Card>

      <Card>
        <CardHeader
          title={t("request.title")}
          description={t("request.description")}
        />
        <div className="space-y-3 px-4 py-4">
          {requestError && <Alert>{requestError}</Alert>}
          <Button
            loading={request.isPending}
            disabled={!affiliate.payoutAddress || belowMinimum || Boolean(openPayout)}
            onClick={() => void handleRequest()}
          >
            {t("request.button", { amount: formatMoney(balance.payableMicros) })}
          </Button>
          {/*
           * The reason the button is off, said next to it. A disabled control
           * with no explanation is the single most common way a dashboard makes
           * somebody write to support.
           */}
          {openPayout ? (
            <p className="text-[12px] text-fg-subtle">
              {t("request.inProgress", { amount: formatMoney(openPayout.amountMicros, openPayout.currency) })}
            </p>
          ) : !affiliate.payoutAddress ? (
            <p className="text-[12px] text-fg-subtle">{t("request.addWallet")}</p>
          ) : belowMinimum ? (
            <p className="text-[12px] text-fg-subtle">
              {t("request.minimum", { minimum: formatMoney(program.minPayoutMicros) })}
            </p>
          ) : null}
        </div>
      </Card>

      <Card>
        <CardHeader title={t("history")} />
        {payouts.isPending ? (
          <LoadingBlock />
        ) : payouts.error ? (
          <div className="px-4 py-4">
            <Alert>{payouts.error.message}</Alert>
          </div>
        ) : payoutRows.length === 0 ? (
          <EmptyState title={t("noPayouts")} />
        ) : (
          <Table
            head={
              <tr>
                <Th>{t("th.requested")}</Th>
                <Th>{t("th.status")}</Th>
                <Th className="text-right">{t("th.amount")}</Th>
                <Th className="text-right" />
              </tr>
            }
          >
            {payoutRows.map((payout) => {
              const state = PAYOUT_STATES[payout.status];
              return (
                <tr key={payout.id}>
                  <Td className="whitespace-nowrap">{formatDate(payout.requestedAt)}</Td>
                  <Td>
                    <div className="space-y-0.5">
                      <StatusBadge tone={state.tone} label={ts(state.label)} />
                      {/* The failure reason, which is the half somebody can act
                          on: a reverted transfer and an address that cannot
                          receive are different problems. */}
                      {payout.failureReason && (
                        <p className="text-[12px] text-fg-subtle">{payout.failureReason}</p>
                      )}
                      {!payout.failureReason && state.hint && (
                        <p className="text-[12px] text-fg-subtle">{ts(state.hint)}</p>
                      )}
                      {/*
                       * The receipt lives in this cell rather than its own
                       * column, and that is a correction rather than a
                       * preference: as a column it carried `priority="sm"` and
                       * vanished below 640px, which hid the *only* proof an
                       * affiliate has that they were paid — on the device most
                       * of them will read this on.
                       */}
                      {payout.txHash && <TxLink payout={payout} />}
                    </div>
                  </Td>
                  <Td className="text-right tabular-nums">
                    {formatMoney(payout.amountMicros, payout.currency)}
                  </Td>
                  <Td className="text-right">
                    {/* Hidden rather than disabled past `requested`: a disabled
                        control invites somebody to work out how to enable it,
                        and past that point somebody may already have sent the
                        transfer. */}
                    {canCancel(payout.status) && (
                      <Button
                        variant="ghost"
                        size="sm"
                        loading={cancel.isPending && cancel.variables === payout.id}
                        onClick={() => cancel.mutate(payout.id)}
                      >
                        {t("cancel")}
                      </Button>
                    )}
                  </Td>
                </tr>
              );
            })}
          </Table>
        )}
        <LoadMore
          hasMore={payouts.hasNextPage}
          loading={payouts.isFetchingNextPage}
          onMore={() => void payouts.fetchNextPage()}
          loaded={payoutRows.length}
          noun="payouts"
        />
      </Card>
    </>
  );
}
